/**
 * Payment Module Documentation
 */

export const paymentsDoc = {
  title: 'Payment Module',
  content: `# Payment Module Training Guide

## 1. Overview

The **Payment Module** manages all money flowing in and out of your business:
- **Sales Payments** (money IN) - From customers
- **Expense Payments** (money OUT) - To vendors

**Key Concept:** Payments link to invoices (sales) or bills (expenses). Recording a payment updates the balance.

---

## 2. Money Flow Overview

\`\`\`mermaid
flowchart LR
    subgraph IN["💰 Money IN (Sales)"]
        CUST[Customer] --> INV[Invoice]
        INV --> PAY_IN[Payment Received]
        PAY_IN --> BANK_IN[Bank Account]
    end
    
    subgraph OUT["💸 Money OUT (Expenses)"]
        BANK_OUT[Bank Account] --> PAY_OUT[Payment Made]
        PAY_OUT --> BILL[Bill]
        BILL --> VEND[Vendor]
    end
    
    style CUST fill:#86efac
    style BANK_IN fill:#22c55e,color:#fff
    style BANK_OUT fill:#ef4444,color:#fff
    style VEND fill:#fca5a5
\`\`\`

---

## 3. Accessing Payments

**Navigation:** Admin Panel → Expenses → **Payments**

---

## 4. Payment Types

### Sales Payments (Inbound)

\`\`\`mermaid
flowchart LR
    subgraph Types["Payment Sources"]
        A[Invoice Payment]
        B[Prepayment/Deposit]
        C[Overpayment → Credit]
    end
    
    A --> ACCT[Customer Account]
    B --> ACCT
    C --> ACCT
    
    style A fill:#86efac
    style B fill:#fef08a
    style C fill:#c4b5fd
\`\`\`

| Source | Description |
|--------|-------------|
| **Invoice Payment** | Customer pays an invoice |
| **Prepayment** | Deposit before invoice |
| **Overpayment** | Excess becomes credit |

### Expense Payments (Outbound)

| Source | Description |
|--------|-------------|
| **Bill Payment** | Pay a vendor bill |
| **Direct Payment** | Payment without bill |

---

## 5. Payment Methods

| Method | Description | Use Case |
|--------|-------------|----------|
| **Check** | Paper check | Vendors, traditional customers |
| **ACH** | Bank transfer | Recurring, large amounts |
| **Credit Card** | Card payment | Quick payments |
| **Cash** | Physical cash | Walk-ins, small purchases |
| **Wire Transfer** | Bank wire | International, large transactions |

---

## 6. Recording Sales Payments

### Payment Flow from Order

\`\`\`mermaid
flowchart TB
    A[Open Order] --> B{Invoice Issued?}
    B -->|No| C[Issue Invoice First]
    C --> B
    B -->|Yes| D[Click Record Payment]
    D --> E[Select Payment Method]
    E --> F[Enter Amount]
    F --> G[Add Reference Number]
    G --> H[Save Payment]
    H --> I{Full Amount?}
    I -->|Yes| J[Invoice → PAID]
    I -->|No| K[Partial Payment]
    K --> L[Balance Remaining]
    
    style A fill:#e8d5c4
    style D fill:#3b82f6,color:#fff
    style J fill:#22c55e,color:#fff
    style L fill:#fef08a
\`\`\`

### From Order Page (Sales)

1. Open Order with issued invoice
2. Click **"Record Payment"** (green button)
3. Select **Payment Method**
4. Enter **Amount**
5. Add **Reference** (check #, confirmation)
6. Click **"Save"**

### From Bill (Expense)

1. Open vendor **Bill**
2. Click **"Record Payment"**
3. Select **Payment Method**
4. Enter **Amount** and **Reference**
5. Click **"Save"**

---

## 7. Payment Status Flow

\`\`\`mermaid
flowchart LR
    DRAFT[📝 Draft] --> POSTED[✅ Posted] --> CLEARED[🏦 Cleared] --> RECON[📊 Reconciled]
    POSTED --> VOID[❌ Void]
    
    style DRAFT fill:#e5e7eb
    style POSTED fill:#93c5fd
    style CLEARED fill:#c4b5fd
    style RECON fill:#86efac
    style VOID fill:#fca5a5
\`\`\`

| Status | Description |
|--------|-------------|
| **Draft** | Not finalized |
| **Posted** | Affects balances |
| **Cleared** | Bank confirmed |
| **Reconciled** | Matched in reconciliation |
| **Void** | Cancelled/reversed |

---

## 8. Partial Payments

\`\`\`mermaid
flowchart LR
    INV["Invoice: $1,000"] --> PAY["Payment: $600"]
    PAY --> BAL["Balance: $400"]
    BAL --> STAT["Status: ISSUED"]
    
    style INV fill:#c4b5fd
    style PAY fill:#86efac
    style BAL fill:#fef08a
\`\`\`

When customer pays less than invoice:

1. Record **actual amount received**
2. Apply to invoice
3. Invoice shows **remaining balance**
4. Stays **Issued** until fully paid

**Example:** Invoice $1,000 → Payment $600 → Balance $400

---

## 9. Overpayments

\`\`\`mermaid
flowchart LR
    INV["Invoice: $1,000"] --> PAY["Payment: $1,200"]
    PAY --> APPLY["Apply $1,000"]
    APPLY --> CREDIT["Credit: $200"]
    CREDIT --> FUTURE["Apply to Future Invoice"]
    
    style INV fill:#c4b5fd
    style PAY fill:#86efac
    style CREDIT fill:#3b82f6,color:#fff
\`\`\`

When customer pays more than owed:

1. Record full amount
2. Apply to invoice(s)
3. Excess becomes **Unapplied Credit**
4. Apply credit to future invoices

**Example:** Invoice $1,000 → Payment $1,200 → Credit $200

---

## 10. Check Management

### Check Workflow

\`\`\`mermaid
flowchart LR
    A[Record Payment] --> B[Method: Check]
    B --> C[Go to Checks]
    C --> D[Find Pending]
    D --> E[Create Check]
    E --> F[Select Bank]
    F --> G[Print Check]
    G --> H[Mark Cleared]
    
    style A fill:#e8d5c4
    style E fill:#3b82f6,color:#fff
    style G fill:#fef08a
    style H fill:#86efac
\`\`\`

### Writing a Check

1. Record payment with **Method: Check**
2. Go to **Expenses → Checks**
3. Find in **"Pending Checks"**
4. Click **"Create Check"**
5. Select **Bank Account**
6. Click **"Print Check"**

---

## 11. Voiding Payments

\`\`\`mermaid
flowchart LR
    A[Open Payment] --> B[Click Void]
    B --> C[Confirm]
    C --> D[Payment Voided]
    D --> E[Balance Restored]
    
    style A fill:#e8d5c4
    style B fill:#ef4444,color:#fff
    style E fill:#fef08a
\`\`\`

To cancel/reverse a payment:

1. Open the Payment
2. Click **"Void Payment"**
3. Confirm void
4. Invoice/Bill balance restored

**Warning:** Voiding cleared payments affects bank reconciliation.

---

## 12. Tips & Best Practices

### Do's ✅

- Always include a reference
- Apply payments to specific invoices/bills
- Record payments promptly
- Reconcile bank accounts monthly

### Don'ts ❌

- Don't leave payments unapplied
- Don't void reconciled payments
- Don't backdate significantly
- Don't skip the reference field

---

## 13. Common Issues

| Issue | Solution |
|-------|----------|
| Payment not applying | Check if invoice is issued |
| Wrong amount | Edit or void and re-enter |
| Can't void payment | May be reconciled - contact admin |
| Overpayment showing | Apply to open invoices or refund |

---

*Last Updated: February 2026*
`
};
